import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  Tabs,
  Tab,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  makeStyles,
  Tooltip,
  Badge,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import RefreshIcon from '@material-ui/icons/Refresh';
import SecurityIcon from '@material-ui/icons/Security';
import CloudQueueIcon from '@material-ui/icons/CloudQueue';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import DescriptionIcon from '@material-ui/icons/Description';
import DashboardIcon from '@material-ui/icons/Dashboard';
import SettingsIcon from '@material-ui/icons/Settings';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import StopIcon from '@material-ui/icons/Stop';
import HistoryIcon from '@material-ui/icons/History';
import CodeIcon from '@material-ui/icons/Code';
import PersonIcon from '@material-ui/icons/Person';
import LockIcon from '@material-ui/icons/Lock';
import LinkIcon from '@material-ui/icons/Link';
import { useApi } from '@backstage/core-plugin-api';
import { kubernetesApiRef } from '@backstage/plugin-kubernetes';
import { PipelineExecutionMonitor } from '../PipelineExecutionMonitor';
import { ArgoTektonIntegration } from '../ArgoTektonIntegration';

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  tabsContainer: {
    marginBottom: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
  },
  tabPanel: {
    padding: theme.spacing(2),
  },
  statusCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  cardContent: {
    flexGrow: 1,
  },
  statusChip: {
    fontWeight: 'bold',
  },
  actionButton: {
    marginLeft: theme.spacing(1),
  },
  tableContainer: {
    maxHeight: 440,
  },
  dialogContent: {
    minWidth: 500,
  },
  formControl: {
    marginTop: theme.spacing(2),
    minWidth: '100%',
  },
  metric: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  metricValue: {
    fontWeight: 'bold',
    fontSize: '1.2rem',
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
}));

// Tab 面板组件
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`control-plane-tabpanel-${index}`}
      aria-labelledby={`control-plane-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

// ServiceAccount 管理界面
const ServiceAccountManager: React.FC = () => {
  const classes = useStyles();
  const kubernetesApi = useApi(kubernetesApiRef);
  const [serviceAccounts, setServiceAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    namespace: 'tekton-pipelines',
    roleBindings: [] as string[],
  });

  const loadServiceAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await kubernetesApi.proxy({
        clusterName: 'local-cluster',
        path: '/api/v1/namespaces/tekton-pipelines/serviceaccounts',
      });

      if (response.ok) {
        const data = await response.json();
        setServiceAccounts(data.items || []);
      }
    } catch (error) {
      // Failed to load service accounts
    } finally {
      setLoading(false);
    }
  }, [kubernetesApi]);

  useEffect(() => {
    loadServiceAccounts();
  }, [loadServiceAccounts]);

  const handleCreate = async () => {
    try {
      const saObject = {
        apiVersion: 'v1',
        kind: 'ServiceAccount',
        metadata: {
          name: formData.name,
          namespace: formData.namespace,
          labels: {
            'app.kubernetes.io/managed-by': 'backstage',
            'tekton.dev/control-plane': 'true',
          },
        },
      };

      await kubernetesApi.proxy({
        clusterName: 'local-cluster',
        path: `/api/v1/namespaces/${formData.namespace}/serviceaccounts`,
        init: {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(saObject),
        },
      });

      setDialogOpen(false);
      loadServiceAccounts();
    } catch (error) {
      // Failed to create service account
    }
  };

  const handleDelete = async (sa: any) => {
    // TODO: Add confirmation dialog
    const confirmed = true; // Replace with proper dialog
    if (!confirmed) return;

    try {
      await kubernetesApi.proxy({
        clusterName: 'local-cluster',
        path: `/api/v1/namespaces/${sa.metadata.namespace}/serviceaccounts/${sa.metadata.name}`,
        init: { method: 'DELETE' },
      });
      loadServiceAccounts();
    } catch (error) {
      // Failed to delete service account
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box className={classes.sectionTitle}>
        <SecurityIcon />
        <Typography variant="h6">Service Accounts</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          size="small"
          style={{ marginLeft: 'auto' }}
        >
          创建 ServiceAccount
        </Button>
      </Box>

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>名称</TableCell>
              <TableCell>命名空间</TableCell>
              <TableCell>创建时间</TableCell>
              <TableCell>Secrets</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {serviceAccounts.map(sa => (
              <TableRow key={sa.metadata.uid}>
                <TableCell>
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <PersonIcon fontSize="small" />
                    {sa.metadata.name}
                  </Box>
                </TableCell>
                <TableCell>{sa.metadata.namespace}</TableCell>
                <TableCell>
                  {new Date(sa.metadata.creationTimestamp).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Badge badgeContent={sa.secrets?.length || 0} color="primary">
                    <VpnKeyIcon fontSize="small" />
                  </Badge>
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleDelete(sa)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>创建 ServiceAccount</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <TextField
            label="名称"
            fullWidth
            margin="normal"
            value={formData.name}
            onChange={e => setFormData({ ...formData, name: e.target.value })}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>命名空间</InputLabel>
            <Select
              value={formData.namespace}
              onChange={e => setFormData({ ...formData, namespace: e.target.value as string })}
            >
              <MenuItem value="tekton-pipelines">tekton-pipelines</MenuItem>
              <MenuItem value="default">default</MenuItem>
              <MenuItem value="payment">payment</MenuItem>
              <MenuItem value="order">order</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>取消</Button>
          <Button onClick={handleCreate} color="primary" variant="contained">
            创建
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Pipeline 模板管理
const PipelineTemplateManager: React.FC = () => {
  const classes = useStyles();
  const [templates] = useState([
    {
      name: 'build-deploy-template',
      description: '标准的构建和部署流程',
      params: ['image_tag', 'namespace'],
      tasks: ['git-clone', 'build', 'push', 'deploy'],
      usage: 12,
    },
    {
      name: 'test-template',
      description: '测试流程模板',
      params: ['test_suite', 'environment'],
      tasks: ['git-clone', 'test', 'report'],
      usage: 8,
    },
    {
      name: 'rollback-template',
      description: 'ArgoCD 回滚模板',
      params: ['app_name', 'revision'],
      tasks: ['validate', 'rollback', 'verify'],
      usage: 3,
    },
  ]);

  return (
    <Box>
      <Box className={classes.sectionTitle}>
        <DescriptionIcon />
        <Typography variant="h6">Pipeline 模板</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          size="small"
          style={{ marginLeft: 'auto' }}
        >
          创建模板
        </Button>
      </Box>

      <Grid container spacing={3}>
        {templates.map(template => (
          <Grid item xs={12} md={6} lg={4} key={template.name}>
            <Card>
              <CardHeader
                title={template.name}
                subheader={template.description}
                action={
                  <Chip label={`${template.usage} 次使用`} size="small" color="primary" />
                }
              />
              <CardContent>
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    参数
                  </Typography>
                  <Box display="flex" style={{ gap: 4 }}>
                    {template.params.map(param => (
                      <Chip key={param} label={param} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    任务
                  </Typography>
                  <Box display="flex" style={{ gap: 4 }}>
                    {template.tasks.map(task => (
                      <Chip key={task} label={task} size="small" />
                    ))}
                  </Box>
                </Box>
                <Box display="flex" style={{ gap: 8 }}>
                  <Button size="small" startIcon={<CodeIcon />}>
                    查看 YAML
                  </Button>
                  <Button size="small" startIcon={<PlayArrowIcon />} color="primary">
                    使用模板
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// 资源配额管理
const ResourceQuotaManager: React.FC = () => {
  const classes = useStyles();
  const [quotas] = useState([
    {
      namespace: 'tekton-pipelines',
      limits: { cpu: '100', memory: '50Gi', pvc: '10' },
      used: { cpu: '45', memory: '23Gi', pvc: '4' },
    },
    {
      namespace: 'payment',
      limits: { cpu: '50', memory: '25Gi', pvc: '5' },
      used: { cpu: '20', memory: '10Gi', pvc: '2' },
    },
    {
      namespace: 'order',
      limits: { cpu: '50', memory: '25Gi', pvc: '5' },
      used: { cpu: '30', memory: '15Gi', pvc: '3' },
    },
  ]);

  const getUsagePercentage = (used: string, limit: string): number => {
    const usedNum = parseFloat(used);
    const limitNum = parseFloat(limit);
    return Math.round((usedNum / limitNum) * 100);
  };

  const getUsageColor = (percentage: number): 'primary' | 'secondary' | 'default' => {
    if (percentage < 60) return 'primary';
    if (percentage < 80) return 'secondary';
    return 'default';
  };

  return (
    <Box>
      <Box className={classes.sectionTitle}>
        <CloudQueueIcon />
        <Typography variant="h6">资源配额</Typography>
      </Box>

      <Grid container spacing={3}>
        {quotas.map(quota => (
          <Grid item xs={12} md={6} lg={4} key={quota.namespace}>
            <Card className={classes.statusCard}>
              <CardHeader
                title={quota.namespace}
                action={
                  <IconButton size="small">
                    <EditIcon fontSize="small" />
                  </IconButton>
                }
              />
              <CardContent className={classes.cardContent}>
                <Box className={classes.metric}>
                  <Typography variant="body2">CPU:</Typography>
                  <Typography className={classes.metricValue}>
                    {quota.used.cpu} / {quota.limits.cpu} cores
                  </Typography>
                  <Chip
                    label={`${getUsagePercentage(quota.used.cpu, quota.limits.cpu)}%`}
                    size="small"
                    color={getUsageColor(getUsagePercentage(quota.used.cpu, quota.limits.cpu))}
                  />
                </Box>
                <Box className={classes.metric}>
                  <Typography variant="body2">内存:</Typography>
                  <Typography className={classes.metricValue}>
                    {quota.used.memory} / {quota.limits.memory}
                  </Typography>
                  <Chip
                    label={`${getUsagePercentage(quota.used.memory, quota.limits.memory)}%`}
                    size="small"
                    color={getUsageColor(getUsagePercentage(quota.used.memory, quota.limits.memory))}
                  />
                </Box>
                <Box className={classes.metric}>
                  <Typography variant="body2">PVC:</Typography>
                  <Typography className={classes.metricValue}>
                    {quota.used.pvc} / {quota.limits.pvc}
                  </Typography>
                  <Chip
                    label={`${getUsagePercentage(quota.used.pvc, quota.limits.pvc)}%`}
                    size="small"
                    color={getUsageColor(getUsagePercentage(quota.used.pvc, quota.limits.pvc))}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// Pipeline 运行历史
const PipelineRunHistory: React.FC = () => {
  const classes = useStyles();
  const [runs] = useState([
    {
      name: 'build-deploy-run-2024-01-20',
      pipeline: 'build-deploy-pipeline',
      namespace: 'tekton-pipelines',
      status: 'Succeeded',
      startTime: '2024-01-20T10:30:00Z',
      duration: '5m 23s',
      triggeredBy: 'backstage-ui',
    },
    {
      name: 'test-run-2024-01-20',
      pipeline: 'test-pipeline',
      namespace: 'tekton-pipelines',
      status: 'Failed',
      startTime: '2024-01-20T09:15:00Z',
      duration: '3m 45s',
      triggeredBy: 'webhook',
    },
    {
      name: 'rollback-run-2024-01-19',
      pipeline: 'rollback-pipeline',
      namespace: 'payment',
      status: 'Running',
      startTime: '2024-01-19T15:45:00Z',
      duration: '2m 10s',
      triggeredBy: 'manual',
    },
  ]);

  const getStatusColor = (status: string): 'primary' | 'secondary' | 'default' => {
    switch (status) {
      case 'Succeeded':
        return 'primary';
      case 'Running':
        return 'secondary';
      case 'Failed':
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Box className={classes.sectionTitle}>
        <HistoryIcon />
        <Typography variant="h6">运行历史</Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          size="small"
          style={{ marginLeft: 'auto' }}
        >
          刷新
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>名称</TableCell>
              <TableCell>Pipeline</TableCell>
              <TableCell>命名空间</TableCell>
              <TableCell>状态</TableCell>
              <TableCell>开始时间</TableCell>
              <TableCell>时长</TableCell>
              <TableCell>触发方式</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.map(run => (
              <TableRow key={run.name}>
                <TableCell>{run.name}</TableCell>
                <TableCell>{run.pipeline}</TableCell>
                <TableCell>{run.namespace}</TableCell>
                <TableCell>
                  <Chip
                    label={run.status}
                    size="small"
                    color={getStatusColor(run.status)}
                  />
                </TableCell>
                <TableCell>
                  {new Date(run.startTime).toLocaleString('zh-CN')}
                </TableCell>
                <TableCell>{run.duration}</TableCell>
                <TableCell>
                  <Chip label={run.triggeredBy} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Tooltip title="查看日志">
                    <IconButton size="small">
                      <DescriptionIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {run.status === 'Running' && (
                    <Tooltip title="停止">
                      <IconButton size="small">
                        <StopIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// 主控制平面组件
export const TektonControlPlane: React.FC = () => {
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <div className={classes.root}>
      {/* 页面标题 */}
      <Box className={classes.header}>
          <Box display="flex" alignItems="center" style={{ gap: 16 }}>
          <DashboardIcon fontSize="large" color="primary" />
          <Typography variant="h4">Tekton Control Plane</Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            className={classes.actionButton}
          >
            刷新
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SettingsIcon />}
            className={classes.actionButton}
          >
            设置
          </Button>
        </Box>
      </Box>

      {/* 概览卡片 */}
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Pipelines
                  </Typography>
                  <Typography variant="h4">24</Typography>
                </Box>
                <AccountTreeIcon fontSize="large" color="primary" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    运行中
                  </Typography>
                  <Typography variant="h4">3</Typography>
                </Box>
                <PlayArrowIcon fontSize="large" color="secondary" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    ServiceAccounts
                  </Typography>
                  <Typography variant="h4">8</Typography>
                </Box>
                <PersonIcon fontSize="large" color="primary" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    命名空间
                  </Typography>
                  <Typography variant="h4">5</Typography>
                </Box>
                <CloudQueueIcon fontSize="large" color="primary" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tab 导航 */}
      <Paper className={classes.tabsContainer}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="执行监控" icon={<DashboardIcon />} />
          <Tab label="ArgoCD 集成" icon={<LinkIcon />} />
          <Tab label="权限管理" icon={<SecurityIcon />} />
          <Tab label="Pipeline 模板" icon={<DescriptionIcon />} />
          <Tab label="资源配额" icon={<CloudQueueIcon />} />
          <Tab label="运行历史" icon={<HistoryIcon />} />
        </Tabs>

        <Box className={classes.tabPanel}>
          <TabPanel value={activeTab} index={0}>
            <PipelineExecutionMonitor />
          </TabPanel>
          <TabPanel value={activeTab} index={1}>
            <ArgoTektonIntegration />
          </TabPanel>
          <TabPanel value={activeTab} index={2}>
            <ServiceAccountManager />
          </TabPanel>
          <TabPanel value={activeTab} index={3}>
            <PipelineTemplateManager />
          </TabPanel>
          <TabPanel value={activeTab} index={4}>
            <ResourceQuotaManager />
          </TabPanel>
          <TabPanel value={activeTab} index={5}>
            <PipelineRunHistory />
          </TabPanel>
        </Box>
      </Paper>

      {/* 快速操作面板 */}
      <Box mt={3}>
        <Typography variant="h6" gutterBottom>
          快速操作
        </Typography>
        <Grid container spacing={2}>
          <Grid item>
            <Button variant="outlined" startIcon={<AddIcon />}>
              创建 Pipeline
            </Button>
          </Grid>
          <Grid item>
            <Button variant="outlined" startIcon={<PersonIcon />}>
              添加 ServiceAccount
            </Button>
          </Grid>
          <Grid item>
            <Button variant="outlined" startIcon={<LockIcon />}>
              配置 RBAC
            </Button>
          </Grid>
          <Grid item>
            <Button variant="outlined" startIcon={<CloudQueueIcon />}>
              调整配额
            </Button>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
};

export default TektonControlPlane;
